import {
  ChainOfThought,
  ChainOfThoughtContent,
  ChainOfThoughtHeader,
  ChainOfThoughtStep,
} from '@/components/ai-elements/chain-of-thought';
import { MessageResponse } from '@/components/ai-elements/message';
import {
  HIDDEN_FROM_TRACE_TOOLS,
  RICH_TOOL_NAMES,
  type ToolPartData,
  asToolPart,
  describeToolStep,
  getToolName,
  inProgressToolLabel,
  isSideOfChatToolPart,
  mapToolStepStatus,
  toolNameToLabel,
} from '@/features/assistant';
import type { UIMessage } from 'ai';
import { Wrench } from 'lucide-react';

interface ToolTraceProps {
  /** All parts of an assistant message; we extract the tool calls in order. */
  parts: UIMessage['parts'];
  /** Whether this message is still streaming — a tool call parked at
   *  `input-available` AFTER the stream ends is an error, not a wait. */
  isStreaming?: boolean;
  /** Complete preparation text retained behind this disclosure. */
  preparationNotes?: readonly string[];
}

/**
 * Inline + expandable trace of every tool call on a message.
 *
 * The header summarises the *latest* tool's status (or "Used N tools"
 * when complete). Expanding reveals one step per tool with state +
 * sanitised input. Rich confirmation tools are still rendered as their
 * own components below the trace; this trace shows the trail behind them.
 */
export function ToolTrace({
  parts,
  isStreaming = false,
  preparationNotes,
}: ToolTraceProps) {
  const tools: ToolPartData[] = [];
  for (const part of parts) {
    const tp = asToolPart(part);
    // Skip pure-infrastructure tools (skill loading, UI tour) so they don't
    // leak as cryptic entries like "Meta_load Skill" in the trace.
    if (
      tp &&
      !HIDDEN_FROM_TRACE_TOOLS.has(getToolName(tp)) &&
      !isSideOfChatToolPart(tp)
    ) {
      tools.push(tp);
    }
  }

  if (tools.length === 0) return null;

  // Hide trace entirely when every visible tool has already been
  // rendered as a rich confirmation card (avoids duplicate noise).
  const allRich = tools.every((tp) => RICH_TOOL_NAMES.has(getToolName(tp)));
  if (
    allRich &&
    tools.every((tp) => tp.state === 'output-available') &&
    !preparationNotes?.length
  ) {
    return null;
  }

  const latest = tools[tools.length - 1];
  const headerLabel = buildHeaderLabel(tools, latest);

  return (
    <ChainOfThought>
      <ChainOfThoughtHeader>{headerLabel}</ChainOfThoughtHeader>
      <ChainOfThoughtContent>
        {tools.map((tp, i) => (
          <ChainOfThoughtStep
            key={`${tp.toolCallId}-${i}`}
            icon={Wrench}
            label={toolNameToLabel(getToolName(tp))}
            description={describeToolStep(tp, isStreaming)}
            status={mapToolStepStatus(tp.state, isStreaming)}
          />
        ))}
        {preparationNotes?.length ? (
          <section
            aria-label="Preparation notes"
            className="space-y-2 border-t pt-3"
          >
            <p className="text-xs font-medium text-muted-foreground">
              Preparation notes
            </p>
            {preparationNotes.map((text, index) => (
              <MessageResponse key={`preparation-note-${index}`}>
                {text}
              </MessageResponse>
            ))}
          </section>
        ) : null}
      </ChainOfThoughtContent>
    </ChainOfThought>
  );
}

function buildHeaderLabel(tools: ToolPartData[], latest: ToolPartData): string {
  // TODO(c-02): use defineTool factory's `statusLabel` once the new
  // wire format ships. Until then, derive a label from the tool name.
  const latestLabel = toolNameToLabel(getToolName(latest));
  const allComplete = tools.every((tp) => tp.state === 'output-available');
  if (allComplete) {
    return tools.length === 1
      ? `Used 1 tool: ${latestLabel}`
      : `Used ${tools.length} tools`;
  }
  if (latest.state === 'input-streaming') {
    return inProgressToolLabel('Preparing', latestLabel);
  }
  if (latest.state === 'output-error') {
    return `${latestLabel} errored`;
  }
  return inProgressToolLabel('Running', latestLabel);
}
